import React, { useMemo, useState } from "react";
import { Alert, Box, Select, MenuItem, TextField, IconButton, Chip, SelectChangeEvent, Button, Collapse, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MoveDownIcon from "@mui/icons-material/MoveDown";
import moment from "moment";
import OpsPanel from "../OpsPanel";
import { Token } from "../../theme";
import OpsDateUtils from "../../utils/OpsDateUtils";
import { WORKLOG_DESCRIPTION_MAX_LENGTH } from "../../models/WorkLog";
import { useQuickLog } from "../../hooks/useQuickLog";

interface QuickLogTileProps {
  onWorklogCreated?: () => void;
}

type LogMode = "day" | "week";

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: Token.textTertiary,
  marginBottom: 6,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: Token.surfaceSunken,
    color: Token.textPrimary,
    fontSize: 13,
    borderRadius: Token.radiusControl,
    "& fieldset": {
      borderColor: Token.borderDefault,
    },
    "&:hover fieldset": {
      borderColor: Token.borderStrong,
    },
    "&.Mui-focused fieldset": {
      borderColor: Token.borderFocus,
    },
  },
  "& .MuiInputBase-input": {
    padding: "8px 12px",
  },
};

export const QuickLogTile: React.FC<QuickLogTileProps> = ({ onWorklogCreated }) => {
  const {
    today,
    assignations,
    selectedAssignation,
    selectAssignation,
    holidaysReady,
    recentDescriptions,
    alreadyLoggedToday,
    isCheckingToday,
    isSaving: isLoading,
    description,
    setDescription,
    hours,
    setHours,
    internalDescription,
    setInternalDescription,
    internalHours,
    setInternalHours,
    showInternal,
    setShowInternal,
    logPrimary,
    copyLastWeek,
  } = useQuickLog({ onWorklogCreated });
  const [logMode, setLogMode] = useState<LogMode>("day");

  const todayOptionLabel = useMemo(() => `Today, ${moment().format("ddd D MMM")}`, []);

  const weekOptionLabel = useMemo(() => {
    const days = OpsDateUtils.allowedWeekdaysThisWeekInMonth(today);
    if (days.length === 0) {
      return "This week";
    }

    const displayStart = days[0];
    const displayEnd = days[days.length - 1];
    const range = OpsDateUtils.isSameCalendarDay(displayStart, displayEnd)
      ? moment(displayStart).format("D MMM")
      : `${moment(displayStart).format("D")}–${moment(displayEnd).format("D MMM")}`;
    return `This week · ${range}`;
  }, [today]);

  const selectedAssignationData = useMemo(
    () => assignations.find((a) => a.externalId === selectedAssignation),
    [assignations, selectedAssignation],
  );

  const assignationShortId = useMemo(() => {
    if (!selectedAssignationData?.label) return "";
    const match = selectedAssignationData.label.match(/^(\d+)/);
    return match?.[1] ?? selectedAssignation.slice(0, 8);
  }, [selectedAssignation, selectedAssignationData]);

  const handleAssignationChange = (event: SelectChangeEvent<string>) => {
    selectAssignation(event.target.value);
  };

  const handleHoursChange = (delta: number) => {
    const next = hours + delta;
    if (next >= 0 && next <= 24) {
      setHours(next);
    }
  };

  const handlePrimaryLog = () => logPrimary(logMode);

  const primaryLabel =
    logMode === "week"
      ? alreadyLoggedToday
        ? `Update week (${hours} h/day)`
        : `Log week (${hours} h/day)`
      : alreadyLoggedToday
        ? `Update ${hours} h`
        : `Log ${hours} h`;

  return (
    <OpsPanel
      title="Quick log"
      panelMenu={
        <Box sx={{ py: 1.5 }}>
          <Select
            value={logMode}
            onChange={(e) => setLogMode(e.target.value as LogMode)}
            disabled={isLoading}
            variant="outlined"
            size="small"
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: Token.textSecondary,
              minWidth: 210,
              maxWidth: 280,
              height: 32,
              backgroundColor: "transparent",
              borderRadius: Token.radiusControl,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: Token.borderDefault,
              },
              "&:hover": {
                backgroundColor: Token.surfaceHover,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: Token.borderStrong,
                },
              },
              "&.Mui-focused": {
                backgroundColor: Token.surfaceHover,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: Token.borderStrong,
                  borderWidth: 1,
                },
              },
              "& .MuiSelect-select": {
                py: "6px",
                pl: "12px",
                pr: "36px !important",
                lineHeight: 1.35,
                display: "flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                boxSizing: "border-box",
              },
              "& .MuiSvgIcon-root": {
                fontSize: 18,
                color: Token.textTertiary,
                right: 8,
              },
            }}
          >
            <MenuItem value="day" sx={{ fontSize: 12 }}>
              {todayOptionLabel}
            </MenuItem>
            <MenuItem value="week" sx={{ fontSize: 12 }}>
              {weekOptionLabel}
            </MenuItem>
          </Select>
        </Box>
      }
    >
      <Box style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {alreadyLoggedToday && (
          <Alert
            severity="info"
            sx={{
              py: 0.5,
              backgroundColor: Token.statusInfoBg,
              color: Token.statusInfoFg,
              "& .MuiAlert-icon": { color: Token.statusInfoFg },
            }}
          >
            You have already logged your hours for today. You may edit the information below if necessary.
          </Alert>
        )}

        <Box style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 120px", gap: 12, alignItems: "end" }}>
          <Box style={{ minWidth: 0 }}>
            <div style={fieldLabelStyle}>Assignation</div>
            <Select
              fullWidth
              size="small"
              value={selectedAssignation}
              onChange={handleAssignationChange}
              disabled={isLoading || isCheckingToday || assignations.length === 0}
              displayEmpty
              sx={{
                ...inputSx["& .MuiOutlinedInput-root"],
                height: 36,
                "& .MuiSelect-select": { padding: "8px 12px", fontSize: 13 },
              }}
            >
              {assignations.map((assignation) => (
                <MenuItem key={assignation.externalId} value={assignation.externalId}>
                  {assignation.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <div style={fieldLabelStyle}>Hours</div>
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 36,
                border: `1px solid ${Token.borderDefault}`,
                borderRadius: Token.radiusControl,
                backgroundColor: Token.surfaceSunken,
              }}
            >
              <IconButton
                size="small"
                onClick={() => handleHoursChange(-1)}
                disabled={isLoading || hours <= 0}
                sx={{ color: Token.textSecondary, padding: "4px" }}
              >
                <RemoveIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Box
                style={{
                  minWidth: 40,
                  textAlign: "center",
                  fontSize: 14,
                  fontWeight: 600,
                  color: Token.textPrimary,
                  fontFamily: "var(--font-mono)",
                }}
              >
                {hours}
              </Box>
              <IconButton
                size="small"
                onClick={() => handleHoursChange(1)}
                disabled={isLoading || hours >= 24}
                sx={{ color: Token.textSecondary, padding: "4px" }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>

        <Box>
          <div style={fieldLabelStyle}>Description</div>
          <Box style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <TextField
              fullWidth
              size="small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              inputProps={{ maxLength: WORKLOG_DESCRIPTION_MAX_LENGTH }}
              disabled={isLoading}
              placeholder="What did you work on?"
              sx={inputSx}
            />
            <Tooltip title="Copy description to internal description">
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    setInternalDescription(description);
                    setInternalHours(hours);
                  }}
                  disabled={isLoading || !description.trim()}
                  sx={{
                    mb: "2px",
                    color: Token.textSecondary,
                    border: `1px solid ${Token.borderDefault}`,
                    borderRadius: Token.radiusControl,
                  }}
                >
                  <MoveDownIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Box style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: Token.textTertiary, fontWeight: 600, marginRight: 2 }}>Reuse</span>
          {recentDescriptions.map((tag) => {
            const selected = description === tag;
            return (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onClick={() => setDescription(tag)}
                disabled={isLoading}
                sx={{
                  height: 26,
                  fontSize: 11.5,
                  fontWeight: selected ? 600 : 500,
                  borderRadius: Token.radiusChip,
                  backgroundColor: selected ? Token.surfaceSelected : Token.surfaceRaised,
                  border: `1px solid ${selected ? Token.actionPrimary : Token.borderDefault}`,
                  color: selected ? Token.textPrimary : Token.textSecondary,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: Token.surfaceHover },
                }}
              />
            );
          })}
        </Box>

        <Box
          style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "2px 0" }}
          onClick={() => setShowInternal((v) => !v)}
        >
          <span
            style={{
              fontSize: 11,
              color: Token.textTertiary,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Internal section
          </span>
          {showInternal ? (
            <ExpandLessIcon sx={{ fontSize: 18, color: Token.textTertiary }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 18, color: Token.textTertiary }} />
          )}
        </Box>

        <Collapse in={showInternal}>
          <Box style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 120px", gap: 12, alignItems: "end" }}>
            <Box style={{ minWidth: 0 }}>
              <div style={fieldLabelStyle}>Internal description</div>
              <TextField
                fullWidth
                size="small"
                value={internalDescription}
                onChange={(e) => setInternalDescription(e.target.value)}
                inputProps={{ maxLength: WORKLOG_DESCRIPTION_MAX_LENGTH }}
                disabled={isLoading}
                sx={inputSx}
              />
            </Box>

            <Box>
              <div style={fieldLabelStyle}>Internal hours</div>
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 36,
                  border: `1px solid ${Token.borderDefault}`,
                  borderRadius: Token.radiusControl,
                  backgroundColor: Token.surfaceSunken,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => setInternalHours(Math.max(0, internalHours - 1))}
                  disabled={isLoading || internalHours <= 0}
                  sx={{ color: Token.textSecondary, padding: "4px" }}
                >
                  <RemoveIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Box
                  style={{
                    minWidth: 40,
                    textAlign: "center",
                    fontSize: 14,
                    fontWeight: 600,
                    color: Token.textPrimary,
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {internalHours}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setInternalHours(Math.min(24, internalHours + 1))}
                  disabled={isLoading || internalHours >= 24}
                  sx={{ color: Token.textSecondary, padding: "4px" }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Collapse>

        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginTop: 4,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 11.5, color: Token.textTertiary, lineHeight: 1.4 }}>
            {assignationShortId
              ? `Saved to worklog ${assignationShortId} · visible to your project manager`
              : "Visible to your project manager"}
          </span>

          <Box style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            <Button
              size="small"
              onClick={copyLastWeek}
              disabled={isLoading || !holidaysReady}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12.5,
                color: Token.textPrimary,
                border: `1px solid ${Token.borderDefault}`,
                borderRadius: Token.radiusControl,
                padding: "6px 12px",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: Token.surfaceHover,
                  borderColor: Token.borderStrong,
                },
              }}
            >
              Copy last week
            </Button>
            <Button
              size="small"
              onClick={handlePrimaryLog}
              disabled={
                isLoading ||
                isCheckingToday ||
                !selectedAssignation ||
                !description.trim() ||
                hours === 0 ||
                (logMode === "week" && !holidaysReady)
              }
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12.5,
                color: Token.textOnAction,
                backgroundColor: Token.actionPrimary,
                borderRadius: Token.radiusControl,
                padding: "6px 14px",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: Token.actionPrimaryHover,
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  color: Token.textTertiary,
                  backgroundColor: Token.surfaceActive,
                },
              }}
            >
              {primaryLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    </OpsPanel>
  );
};
